import Editor, { type OnMount } from "@monaco-editor/react";
import type React from "react";
import { useCallback, useRef } from "react";

type Props = {
  value: string;
  onChange: (value: string) => void;
  jsonSchema: Record<string, unknown>;
  schemaUri?: string;
  height?: string;
  readOnly?: boolean;
};

export const DefinitionMonacoEditor: React.FC<Props> = ({
  value,
  onChange,
  jsonSchema,
  schemaUri = "definition-schema",
  height = "500px",
  readOnly = false,
}) => {
  const editorRef = useRef<Parameters<OnMount>[0] | null>(null);

  const handleMount: OnMount = useCallback(
    (editor, monaco) => {
      editorRef.current = editor;

      monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
        validate: true,
        schemas: [
          {
            uri: schemaUri,
            fileMatch: ["*"],
            schema: jsonSchema,
          },
        ],
      });

      setTimeout(() => {
        editor.getAction("editor.action.formatDocument")?.run();
      }, 100);
    },
    [jsonSchema, schemaUri],
  );

  const handleChange = useCallback(
    (value: string | undefined) => {
      if (value !== undefined) {
        onChange(value);
      }
    },
    [onChange],
  );

  return (
    <div className="overflow-hidden rounded-[var(--radius-md)] border border-border-default">
      <Editor
        height={height}
        language="json"
        value={value}
        onChange={handleChange}
        onMount={handleMount}
        options={{
          readOnly,
          minimap: { enabled: false },
          lineNumbers: "on",
          scrollBeyondLastLine: false,
          automaticLayout: true,
          tabSize: 2,
          formatOnPaste: true,
          // Disable EditContext API — Chrome's native EditContext breaks number key
          // input (1-9) when Monaco is rendered inside modal dialogs.
          editContext: false,
        }}
      />
    </div>
  );
};
